import Actions from './actions';
import AnswerCard from './answer-card.jsx';
import AnswerUtil from './answer-util';
import Error from './error.jsx';
import ImmutableRenderComponent from './immutable-render-component';
import QuestionCard from './question-card.jsx';
import Spinner from './spinner.jsx';

import React from 'react';
import Router from 'react-router';
const Link = Router.Link;

/**
 * Represents the Answer Page which displays the following sections:
 * - QuestionCard : selected (asked) question with multiple choices.
 * - AnswerCards : the top one corresponds to the correct answer choice.
 * By default this card should be open (expanded) and it appears under
 * the heading 'Aristo Answered'.
 * This is followed by closed answer cards for the incorrect
 * answer choices under the heading 'Other Answers'.
 */
export default class AnswerPage extends ImmutableRenderComponent {
   constructor(props) {
     super(props);
     this.state = {
       invalidQuestion: false,
       // Controller Response to a question, as obtained from the AnswerStore.
       controllerResponse: undefined,
       // Controller-returned error. This state helps differentiate between when we are still waiting
       // for a response to come back vs when we received a response but it had an error.
       controllerError: false,
     };
     this.onAnswerChangeEvent = this.setAnswerFromAnswerStore.bind(this);
     this.props.answerStore.addChangeListener(this.onAnswerChangeEvent);
   }

  static get propTypes() {
    return {
      modeStore: React.PropTypes.object.isRequired,
      dispatcher: React.PropTypes.object.isRequired,
      answerStore: React.PropTypes.object.isRequired,
      featuredQuestions: React.PropTypes.array.isRequired,
      query: React.PropTypes.object.isRequired,
    };
  }

  /**
   * Sets controller response from AnswerStore state whenever AnswerStore emits a change.
   */
  setAnswerFromAnswerStore(answerStoreState) {
    this.setState({
      controllerResponse: answerStoreState.controllerResponse,
      controllerResponseStatus: answerStoreState.controllerResponseStatus,
      controllerError: answerStoreState.controllerError,
    });
  }

  componentDidMount() {
    if ( this.props.query.q === undefined ) {
      this.setState({ invalidQuestion: true });
    } else {
      // This is the value of the "q?=..." query param, and is the full
      // question text: "What is... (A) foo (B) bar (C) ..."
      const questionText = this.props.query.q;

      // If the question is one of our featured questions, identify which
      // answer is the correct one and start the process of rendering the
      // answer for this question by id.
      const matchingQuestions = this.props.featuredQuestions.filter((q) => q.questionText === questionText);
      if (matchingQuestions.length === 1) {
        const matchingQuestion = matchingQuestions[0];
        this.setState({ correctAnswer: matchingQuestion.correctAnswer });
        this.props.dispatcher.dispatch({ actionType: Actions.REQUEST_ANSWER, id: matchingQuestion.id });
        return;
      }
 
      // Otherwise, start the process of rendering the answer without an id,
      // which will result in an error.
      this.props.dispatcher.dispatch({ actionType: Actions.REQUEST_ANSWER });
    }
  }

  componentWillUnmount() {
    this.props.answerStore.removeChangeListener(this.onAnswerChangeEvent);
  }

  /**
   * Renders the Selected Answer card with the "Aristo Answered" heading.
   */
  renderSelectedAnswer(question, answer) {
   // Specify different thresholds here if desired
   // TODO: Encode these parameters somewhere better, don't copy DA score adjustments from answer-card
   const threshold = answer.selection.multipleChoice ? 0.45 : 0.3;
   const confidence = answer.selection.multipleChoice ?
     answer.confidence : Math.min(answer.confidence, 1.0) * 0.6;
   let answerCardHeading = "Aristo Answered:";
   let confident = true;
   if (confidence < threshold) {
     answerCardHeading = "Aristo's Best Guess:";
     confident = false;
   }
   return (
     <div className="aristo-answered spaced">
       <h2 className="box-header text-center no-bottom-margin">{answerCardHeading}</h2>
       <div className="box-pointer"></div>
       <AnswerCard demoSolvers={this.props.demoSolvers}
         answer={answer}
         question={question}
         initialStateOpened={true}
         confident={confident}
         correctAnswer={this.state.correctAnswer} />
     </div>
   );
  }

  /**
   * Renders Unselected answers with the "Other Answers" heading.
   */
  renderUnselectedAnswers(question, wrongAnswers) {
    const renderedWrongAnswers =
      (wrongAnswers.map((wrongAnswer, i) =>
        <AnswerCard key={i}
          demoSolvers={this.props.demoSolvers}
          answer={wrongAnswer}
          question={question}
          initialStateOpened={false} confident={true} />));

    return (
      <div className="other-answers spaced">
        <h2 className="box-header text-center no-bottom-margin">Other Answers:</h2>
        <div className="box-pointer"></div>
        {renderedWrongAnswers}
      </div>
    );
  }

  /**
   * Renders the QuestionCard and the AnswerCards corresponding to the correct answer choice ("Aristo Answered")
   * and incorrect answer choices ("Other Answers").
   */
  render() {
    // Answer Page JSX to render
    let answerPage = null;

    // Set the current question for display in the entry form.
    const query = { question: this.props.query.q };

    // Button that takes user back to home page with featured questions.
    const askAnotherQuestion =
      (<div className="ask-another-question spaced text-center">
         <Link to="/" query={query} className="button bold">Ask Another Question</Link>
       </div>);

    // Check for errors, populate valid answer from Controller Response in the absence of errors.
    let error;
    let answer;

    if (this.state.controllerResponseStatus === 403) {
      error = "Aristo spotted some profanity in your question and will not attempt to answer it.";
    } else if (this.state.invalidQuestion) {
      error = "The demo is not currently set up to answer this question. Please select from the featured questions.";
    } else if (this.state.controllerError || (this.state.controllerResponse &&
      ((this.state.controllerResponse.response === undefined) || (this.state.controllerResponse.response.success === undefined)))) {
      error = "Aristo is not able to process your request at this time.";
    } else if(this.state.controllerResponse) {
      // Get the Selected and Unselected Answers out of the Controller Response.
      answer = AnswerUtil.selectedAndUnselectedAnswers(this.state.controllerResponse.response.success);
      if (answer.selectedAnswer === undefined) {
        error = "Aristo could not select any answers for this question.";
      } else if (answer.selectedAnswer.selection.multipleChoice && AnswerUtil.isTrueFalseMCQuestion(answer)) {
        error = "Yes/No or True/False questions are not currently handled."
      } else if (answer.selectedAnswer.analyses.length > 0 &&
        answer.selectedAnswer.analyses[0].analysis.error) {
        error = answer.selectedAnswer.analyses[0].analysis.error;
      }
    }

    const questionCard = <QuestionCard question={this.props.query.q}/>;

    if (error) {
      const errorTitle = "Sorry, Aristo could not answer this question!";
      answerPage= (
        <div>
          <div className="aristo">
            {questionCard}
            <Error title={errorTitle} error={error}/>
            {askAnotherQuestion}
          </div>
        </div>);
    } else {
      // Display Spinner if answer has not been received yet,
      // error if the controllerResponse contains failure or if no answer was selected,
      // and the answer cards otherwise.
      const spinner = (answer === undefined) ? <Spinner /> : null;

      const selectedAnswerCard = (answer && answer.selectedAnswer) ? this.renderSelectedAnswer(this.props.query.q, answer.selectedAnswer) : null;
      const otherAnswersCards = (answer && (answer.unselectedAnswers.length)) ? this.renderUnselectedAnswers(this.props.query.q, answer.unselectedAnswers) : null;

      answerPage = (
        <div>
          <div className="aristo">
            {questionCard}
            {spinner}
            {selectedAnswerCard}
            {askAnotherQuestion}
            {otherAnswersCards}

            <div className="disclaimer">
              Disclaimer: Please note that the justification sentences used as part of Aristo’s reasoning are examples pulled from a wide variety of public web sources
              and do not necessarily reflect the views or opinions of the Allen Institute of Artificial Intelligence or its employees.
              The information contained in this demo is for educational and entertainment purposes only.
            </div>
          </div>
        </div>);
    }

    return answerPage;
  }
}
